import React from 'react';
import { IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledAnimatedButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => {
    const borderColor = active ? theme.palette.primary.main : theme.palette.text.secondary;
    const primaryColor = theme.palette.primary.main;
    const textColor = active ? theme.palette.primary.main : theme.palette.text.secondary;

    const buttonSize = 40;
    const borderWidth = 2;
    const buttonRadius = buttonSize / 2;
    const dotSize = 4;
    const dotRadius = dotSize / 2;

    const outerRadius = buttonRadius;
    const innerRadius = buttonRadius - borderWidth;
    const borderCenterRadius = (outerRadius + innerRadius) / 2;
    const pathRadius = borderWidth === 2
        ? borderCenterRadius + 0.25
        : borderCenterRadius;

    return {
        width: buttonSize,
        height: buttonSize,
        color: textColor,
        position: 'relative',
        borderRadius: '50%',
        border: `${borderWidth}px solid ${borderColor}`,
        backgroundColor: active ? theme.palette.primary.main + '10' : 'transparent',
        transition: theme.transitions.create(['all'], {
            duration: theme.transitions.duration.short,
        }),
        overflow: 'visible',
        '&:hover': {
            backgroundColor: active
                ? theme.palette.primary.main + '20'
                : theme.palette.action.hover,
            borderColor: active
                ? theme.palette.primary.dark
                : theme.palette.text.primary,
            color: active
                ? theme.palette.primary.dark
                : theme.palette.text.primary,
        },
        '& .MuiSvgIcon-root': {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '1.25rem',
            transition: theme.transitions.create(['all'], {
                duration: theme.transitions.duration.short,
            }),
            zIndex: 1,
        },
        '&::before': {
            content: '""',
            position: 'absolute',
            width: dotSize,
            height: dotSize,
            borderRadius: '50%',
            background: primaryColor,
            top: '50%',
            left: '50%',
            marginTop: `-${dotRadius}px`,
            marginLeft: `-${dotRadius}px`,
            transformOrigin: '50% 50%',
            transform: `translateY(-${pathRadius}px)`,
            animation: active
                ? 'rotateDot 2s linear infinite, pulseDot 2s ease-in-out infinite'
                : 'rotateDot 3s linear infinite',
            boxShadow: active
                ? `0 0 12px ${primaryColor}, 0 0 6px ${primaryColor}`
                : `0 0 8px ${primaryColor}`,
            zIndex: 2,
        },
        '@keyframes rotateDot': {
            '0%': {
                transform: `rotate(0deg) translateY(-${pathRadius}px) rotate(0deg)`,
            },
            '100%': {
                transform: `rotate(360deg) translateY(-${pathRadius}px) rotate(-360deg)`,
            },
        },
        '@keyframes pulseDot': {
            '0%, 100%': {
                opacity: 1,
                transform: `translateY(-${pathRadius}px) scale(1)`,
            },
            '50%': {
                opacity: 0.7,
                transform: `translateY(-${pathRadius}px) scale(1.2)`,
            },
        },
    };
});

export const AnimatedAppButton = ({ children, active, ...props }) => {
    return (
        <StyledAnimatedButton active={active} {...props}>
            {children}
        </StyledAnimatedButton>
    );
};

